import { useCallback, useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { PanelLoading } from "../../../components/Loading";
import { useToast } from "../../../components/Toast";
import { useAuth } from "../../../auth/AuthContext";
import type { ApiError } from "../../../api/client";
import { externalLabApi, type ExternalLaboratory } from "../../../api/externalAnalyses";

/**
 * Šifarnik spoljnih laboratorija.
 *
 * Prazan je namerno. Ko radi koju analizu za Bankom nije podatak koji smo mogli da pretpostavimo — u
 * laboratorijskom programu spoljni nalazi se nigde ne vode, pa ništa nije bilo za prenošenje. Direktor
 * kvaliteta upisuje laboratorije koje zaista koriste.
 *
 * Laboratorija se ne briše nego povlači iz upotrebe: nalazi već prijavljeni na nju ostaju, a broj nalaza
 * uz red je ono što kaže koja se stavka još sme preimenovati bez posledica.
 */
export default function ExternalLaboratoriesPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const mayManage = hasPermission("external_labs:manage");
  const canDelete = hasPermission("section:external_labs:full");

  const [rows, setRows] = useState<ExternalLaboratory[] | null>(null);
  const [editing, setEditing] = useState<ExternalLaboratory | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      setRows(await externalLabApi.list(false));
    } catch (e) {
      toast.error((e as ApiError).message);
      setRows([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function retire(l: ExternalLaboratory) {
    if (!window.confirm(t("extLabs.confirmRetire", "Povući {{n}} iz upotrebe?", { n: l.name }))) return;
    try {
      await externalLabApi.retire(l.id);
      toast.success(t("extLabs.retired", "Laboratorija je povučena iz upotrebe."));
      await load();
    } catch (e) {
      toast.error((e as ApiError).message);
    }
  }

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <h1>{t("extLabs.title", "Spoljne laboratorije")}</h1>
          <div className="sub">
            {t("extLabs.lead", "Laboratorije van Bankoma na koje se šalju uzorci. Spisak je preuzet iz registra firmi, pa u njemu stoje i firme koje Bankom ne koristi kao laboratoriju: ugasite ih i više se nigde ne nude. Laboratoriju koje u registru nema upišite rukom.")}
          </div>
        </div>
        {mayManage && !creating && (
          <button type="button" className="btn primary" onClick={() => setCreating(true)}>
            {t("extLabs.new", "Nova laboratorija")}
          </button>
        )}
      </div>

      {mayManage && (creating || editing) && (
        <LabForm
          lab={editing}
          onCancel={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            void load();
          }}
        />
      )}

      {rows === null ? (
        <PanelLoading text={t("extLabs.loading", "Čitam šifarnik…")} />
      ) : rows.length === 0 ? (
        <div className="empty">
          {t("extLabs.none", "Šifarnik je prazan. Upišite laboratorije na koje Bankom šalje uzorke: mikrobiologija se naručuje spolja i nalaz stiže otuda.")}
        </div>
      ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th>{t("extLabs.col.name", "Naziv")}</th>
              <th>{t("extLabs.col.accreditation", "Akreditacija")}</th>
              <th>{t("extLabs.col.reports", "Nalaza")}</th>
              <th>{t("extLabs.col.state", "Stanje")}</th>
              {mayManage && <th />}
            </tr>
          </thead>
          <tbody>
            {rows.map((l) => (
              <tr key={l.id}>
                <td>
                  <strong>{l.name}</strong>
                  {l.note && <div className="muted">{l.note}</div>}
                </td>
                <td>{l.accreditation ?? "—"}</td>
                <td>{l.reports}</td>
                <td>
                  <span className={`tag ${l.active ? "ok" : "neutral"}`}>
                    {l.active ? t("extLabs.active", "u upotrebi") : t("extLabs.retired.tag", "povučena")}
                  </span>
                </td>
                {mayManage && (
                  <td>
                    <button type="button" className="btn ghost sm" onClick={() => setEditing(l)}>
                      {t("common.edit", "Uredi")}
                    </button>
                    {l.active && (
                      <button type="button" className="btn ghost sm" disabled={!canDelete} onClick={() => void retire(l)}>
                        {t("extLabs.retire", "Povuci")}
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function LabForm({
  lab,
  onSaved,
  onCancel,
}: {
  lab: ExternalLaboratory | null;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [name, setName] = useState(lab?.name ?? "");
  const [accreditation, setAccreditation] = useState(lab?.accreditation ?? "");
  const [note, setNote] = useState(lab?.note ?? "");
  const [sortOrder, setSortOrder] = useState(String(lab?.sortOrder ?? 0));
  const [saving, setSaving] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      toast.error(t("extLabs.needName", "Unesite naziv laboratorije."));
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        accreditation: accreditation.trim() || null,
        note: note.trim() || null,
        sortOrder: Number(sortOrder) || 0,
      };
      if (lab) await externalLabApi.update(lab.id, body);
      else await externalLabApi.create(body);
      toast.success(t("extLabs.saved", "Sačuvano."));
      onSaved();
    } catch (e) {
      toast.error((e as ApiError).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="card" onSubmit={submit}>
      <div className="card-h">
        <strong>{lab ? t("extLabs.editTitle", "Uredi laboratoriju") : t("extLabs.new", "Nova laboratorija")}</strong>
      </div>
      <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
          <label>
            <span className="muted">{t("extLabs.col.name", "Naziv")}</span>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label>
            <span className="muted">{t("extLabs.col.accreditation", "Akreditacija")}</span>
            <input
              className="input"
              value={accreditation}
              onChange={(e) => setAccreditation(e.target.value)}
              placeholder={t("extLabs.accreditationHint", "na primer: ATS 01-123")}
            />
          </label>
          <label>
            <span className="muted">{t("extLabs.col.order", "Redosled")}</span>
            <input className="input" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
          </label>
        </div>
        <label>
          <span className="muted">{t("extLabs.col.note", "Napomena")}</span>
          <input className="input" value={note} onChange={(e) => setNote(e.target.value)} />
        </label>
        <div className="page-actions">
          <button type="submit" className="btn primary" disabled={saving}>
            {saving ? t("common.saving", "Čuvam…") : t("common.save", "Sačuvaj")}
          </button>
          <button type="button" className="btn ghost" onClick={onCancel} disabled={saving}>
            {t("common.cancel", "Odustani")}
          </button>
        </div>
      </div>
    </form>
  );
}
